/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
export type OperatingSystem = 'mac' | 'windows' | 'linux';

export type KbdKey =
  | 'meta'
  | 'shift'
  | 'ctrl'
  | 'enter'
  | 'delete'
  | 'escape'
  | 'tab'
  | 'capslock'
  | 'up'
  | 'right'
  | 'down'
  | 'left'
  | 'pageup'
  | 'pagedown'
  | 'home'
  | 'end'
  | 'space'
  | 'fn'
  | 'alt';

/**
 * Detects the operating system based on the user agent
 */
export function detectOS(): OperatingSystem {
  if (typeof window === 'undefined') {
    return 'linux';
  }

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (userAgent.includes('mac')) {
    return 'mac';
  } else if (userAgent.includes('win')) {
    return 'windows';
  } else {
    return 'linux';
  }
}

/**
 * Returns OS-aware keyboard key symbols
 */
export function getKbdKeysMap(
  os: OperatingSystem = detectOS(),
): Record<KbdKey, string> {
  const baseMap: Record<KbdKey, string> = {
    alt: os === 'mac' ? '⌥' : 'alt',
    capslock: '⇪',
    ctrl: os === 'mac' ? '⌃' : 'ctrl',
    delete: os === 'mac' ? '⌫' : 'delete',
    down: '↓',
    end: 'end',
    enter: '↵',
    escape: '⎋',
    fn: 'Fn',
    home: 'home',
    left: '←',
    meta: os === 'mac' ? '⌘' : os === 'windows' ? '⊞' : 'meta',
    pagedown: '⇟',
    pageup: '⇞',
    right: '→',
    shift: '⇧',
    space: '␣',
    tab: '⇥',
    up: '↑',
  };

  return baseMap;
}

/**
 * Returns OS-aware keyboard key labels
 */
export function getKbdKeysLabelMap(
  os: OperatingSystem = detectOS(),
): Record<KbdKey, string> {
  return {
    alt: 'Alt',
    capslock: 'Caps Lock',
    ctrl: 'Control',
    delete: 'Delete',
    down: 'Down',
    end: 'End',
    enter: 'Enter',
    escape: 'Escape',
    fn: 'Fn',
    home: 'Home',
    left: 'Left',
    meta: os === 'mac' ? 'Command' : os === 'windows' ? 'Windows' : 'Meta',
    pagedown: 'Page Down',
    pageup: 'Page Up',
    right: 'Right',
    shift: 'Shift',
    space: 'Space',
    tab: 'Tab',
    up: 'Up',
  };
}
